import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Card, CardBody, Button, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';

/**
 * Self-chromed shell for the compute storage/misc list pages. Each page owns its
 * own header (ComputeConsole no longer wraps these routes in a service header) and
 * renders one of four states inside a Card: loading skeleton, error + Retry,
 * empty placeholder, or the table. Keeps the six list pages visually consistent.
 */
export function ListPageShell({
  icon,
  eyebrow,
  breadcrumbs,
  title,
  description,
  actions,
  children,
}: {
  icon?: ReactNode;
  eyebrow?: ReactNode;
  breadcrumbs?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Container size="xl">
      <PageHeader
        icon={icon}
        eyebrow={eyebrow}
        breadcrumbs={breadcrumbs}
        title={title}
        description={description}
        actions={actions}
      />
      {children}
    </Container>
  );
}

/** Vertically stacked skeleton rows for table loading state. */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} style={{ height: 44 }} />
        ))}
      </CardBody>
    </Card>
  );
}

/** Error panel with a Retry button — never silently render a failed fetch as empty. */
export function ErrorState({
  title = 'Could not load data',
  description = 'Something went wrong while fetching this list.',
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <Card>
      <CardBody>
        <EmptyState
          icon={<AlertTriangle size={28} color="var(--vn-danger-solid)" />}
          title={title}
          description={description}
          actions={
            onRetry ? (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        />
      </CardBody>
    </Card>
  );
}

/** Shared anchor styling for the "linked instance" cells across list pages. */
export const linkStyle: React.CSSProperties = {
  color: 'var(--vn-accent-text, var(--vn-accent-solid))',
  textDecoration: 'none',
  fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
};

/** Subtle em-dash placeholder for empty cells. */
export const Dash = () => <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;

/** MB → GB (one decimal). Accepts string or number (some endpoints stringify). */
export const mbToGB = (mb?: string | number) => {
  const n = Number(mb);
  return Number.isFinite(n) && n > 0 ? Math.round((n / 1024) * 10) / 10 : 0;
};
